import { useEffect, useState } from 'react';
import {
  canShowExternalPricingForStorefront,
  requiresSubscriptionStorefrontCheck,
} from '../utils/iosExternalBilling';

let cachedExternalPricingAvailability: boolean | null = null;
let externalPricingAvailabilityPromise: Promise<boolean> | null = null;

const loadExternalPricingAvailability = async (): Promise<boolean> => {
  if (cachedExternalPricingAvailability !== null) {
    return cachedExternalPricingAvailability;
  }

  if (!externalPricingAvailabilityPromise) {
    externalPricingAvailabilityPromise = canShowExternalPricingForStorefront()
      .then((isAvailable) => {
        cachedExternalPricingAvailability = isAvailable;
        return isAvailable;
      })
      .finally(() => {
        externalPricingAvailabilityPromise = null;
      });
  }

  return externalPricingAvailabilityPromise;
};

export const useSubscriptionStorefrontAccess = () => {
  const shouldCheckStorefront = requiresSubscriptionStorefrontCheck();
  const [isSubscriptionManagementAvailable, setIsSubscriptionManagementAvailable] =
    useState<boolean>(true);
  const [isExternalPricingAvailable, setIsExternalPricingAvailable] = useState<boolean>(
    !shouldCheckStorefront || cachedExternalPricingAvailability === true
  );
  const [loading, setLoading] = useState<boolean>(
    shouldCheckStorefront && cachedExternalPricingAvailability === null
  );

  useEffect(() => {
    setIsSubscriptionManagementAvailable(true);

    if (!shouldCheckStorefront) {
      setIsExternalPricingAvailable(true);
      setLoading(false);
      return;
    }

    if (cachedExternalPricingAvailability !== null) {
      setIsExternalPricingAvailable(cachedExternalPricingAvailability);
      setLoading(false);
      return;
    }

    let isActive = true;
    setLoading(true);

    void loadExternalPricingAvailability().then((isAvailable) => {
      if (!isActive) {
        return;
      }

      setIsExternalPricingAvailable(isAvailable);
      setLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [shouldCheckStorefront]);

  return {
    isSubscriptionManagementAvailable,
    isExternalPricingAvailable,
    loading,
  };
};

export const useSubscriptionManagementAvailability =
  useSubscriptionStorefrontAccess;
